import { CityFactory } from "../city/city.factory";
import { VenueFactory } from "../venue/venue.factory";
import { OrderFactory } from "../order/order.factory";
import { VenueOrderFactory } from "../order/order.factory.venue";
import { Pass, ProductFactory } from "../product/product.factory";
import { City } from "../city/city.model";
import { Venue } from "../venue/venue.model";
import { AngularFirestore } from "@angular/fire/firestore";
import { CitySummary } from "../order/models/city-summary.model";
import { VenueSummary } from "../order/models/venue-summary.model";
import { ProductType, ScheduleType } from "../product/enums";
import { Product } from "../product/product.model";
import { Observable } from "rxjs";
import { CityBuilder } from "../city/city.builder";
import { Order, MenuOrderData } from "../order/order.model";
import { OrderFactoryPeriodSummary } from "../order/analytics/summaries/summary.abstract";
import {
  OrderFactoryProductData,
  OrderFactoryEventData,
  OrderFactoryDayOfWeekData,
  OrderFactoryCityData,
  OrderFactoryVenueData
} from "../order/analytics/data/data.abstract";
import { User } from "../user/user.model";
import { MenuDrinkFactory } from "../menu/drink/menu.drink.factory";
import { MenuCategoryFactory } from "../menu/category/menu.category.factory";
import { MenuDrink } from "../menu/drink/menu.drink.model";
import { MenuCategory } from "../menu/category/menu.category.model";
import { MenuCategoryBuilder } from "../menu/category/menu.category.builder";
import { MenuDrinkBuilder } from "../menu/drink/menu.drink.builder";
import { TimeframeFilter } from "../timeframe/timeframes";
import { MenuSalesAnalytics } from "../menu/drink/menu.drink.analytics";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { EventFactory } from "../event/event.factory";
import { ToastService } from "ng-uikit-pro-standard";
import { AuthService } from "../services";
import { UserFactory } from "../user/user.factory";
import { environment } from "src/environments/environment";

export class AppState {
  public itemsPerPage: number[] = [10, 25, 50];

  constructor(
    private db: AngularFirestore,
    public user: User,
    public authService: AuthService,
    public httpClient: HttpClient,
    public toast: ToastService
  ) {
    this._venueSearchText = "";
  }

  public async getUserJWT() {
    return await this.authService.user.getIdToken();
  }

  public async getHTTPHeaders() {
    const userJWT = await this.getUserJWT();
    return new HttpHeaders().set("Authorization", `${userJWT}`)
  }

  public async initializeAppState() {
    this._venues = new VenueFactory(
      null,
      this.db,
      this.httpClient,
      this.authService,
      this.toast
    );
    this._orders = new OrderFactory(this.db);
    this._cities = new CityFactory(
      this.db,
      this.httpClient,
      this.authService,
      this.toast
    );
    this._cities = new CityFactory(this.db, this.httpClient, this.authService, this.toast);
    this._events = new EventFactory(null, this.db, this.httpClient, this.authService, this.toast);
    this._users = new UserFactory(this.db, this.httpClient, this.authService);

    const httpHeaders = await this.getHTTPHeaders();
    this.permissions = await this.httpClient
        .get(
            `${environment.apiBaseUrl}/api/v1/permissions`,
            {
                headers: httpHeaders,
                params: {
                    userId: this.user.id,
                },
            },
        )
        .toPromise()
        .then((permissionsResponse) => {
            return permissionsResponse as string[];
        })
        .catch((error) => {
            this.toast.error("Could not get user permissions. Please try again.", "Error", {
                opacity: 1,
            });
            throw(error);
        });
  }

  public async getAuthorizedVenues(): Promise<Venue[]> {
    return await this.getAllVenues().then<Venue[]>(venues =>
      venues.filter(
        venue =>
          venue.city != null &&
          (this.user.admin.cities.includes(venue.city.path) ||
            this.user.admin.venues.includes(venue.path))
      )
    );
  }

  public get isAdmin(): boolean {
    return this.user.isAdmin;
  }

  public onAdminTabPressed() {
    this._selectedVenue = null;
    this._selectedCity = null;
    this._venues = new VenueFactory(
      null,
      this.db,
      this.httpClient,
      this.authService,
      this.toast
    );
    this._orders = new OrderFactory(this.db);
  }
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /**
   *
   * BEGIN ORDER & ANALYTICS SECTION
   *
   */
  protected _orders: OrderFactory | VenueOrderFactory;

  public onExportVenueOrdersPressed(orders: Order[]) {
    return this._orders.export(orders, this.isAdmin);
  }
  public onExportFormQuestionsPressed(orders: Order[]) {
    return this._orders.exportCustomQuestions(orders, this.isAdmin);
  }

  public get orders$(): Observable<Order[]> {
    return this._orders.orders$;
  }
  public get periodTotalData$(): Observable<OrderFactoryPeriodSummary> {
    return this._orders.analytics.period$;
  }
  public get productTableData$(): Observable<OrderFactoryProductData[]> {
    return this._orders.analytics.product$;
  }
  public get eventTableData$(): Observable<OrderFactoryEventData[]> {
    return this._orders.analytics.event$;
  }
  public get dowTableData$(): Observable<OrderFactoryDayOfWeekData[]> {
    return this._orders.analytics.dow$;
  }
  public get cityTableData$(): Observable<OrderFactoryCityData[]> {
    return (this._orders as OrderFactory).analytics.city$;
  }
  public get venueTableData$(): Observable<OrderFactoryVenueData[]> {
    return (this._orders as OrderFactory).analytics.venue$;
  }
  public get orderFilterCities(): CitySummary[] {
    return (this._orders as OrderFactory).cities.sort((one, two) =>
      one.name > two.name ? 1 : -1
    );
  }
  public get orderFilterVenues(): VenueSummary[] {
    return (this._orders as OrderFactory).venues.sort((one, two) =>
      one.name > two.name ? 1 : -1
    );
  }
  public get orderFilterTimeframes(): TimeframeFilter[] {
    return this._orders.timeframes;
  }
  public get orderFilterCurrentVenue(): VenueSummary {
    return this._orders.currentVenue;
  }
  public get orderFilterCurrentProductType(): ProductType {
    return this._orders.currentProductType;
  }
  public get orderFilterCurrentCity(): CitySummary {
    return this._orders.currentCity;
  }
  public get orderFilterCurrentTimeframe(): TimeframeFilter {
    return this._orders.currentTimeframe;
  }

  public get orderFilterShowRedeemed(): boolean {
    return this._orders.showRedeemed;
  }

  public get orderFilterShowOutstanding(): boolean {
    return this._orders.showOutstanding;
  }

  public get orderFilterSearchText(): string {
    return this._orders.currentSearchText;
  }

  public get orderTableTotalOrders(): number {
    return this._orders.totalOrders;
  }

  public onOrderFilterCitySelected = (city: CitySummary) =>
    (this._orders as OrderFactory).setCityFilter(city);
  public onOrderFilterVenueSelected = (venue: VenueSummary) =>
    (this._orders as OrderFactory).setVenueFilter(venue);
  public onOrderFilterProductTypeSelected = (type: ProductType) =>
    this._orders.setProductTypeFilter(type);
  public onOrderFilterSearchChanged = (text: string) =>
    this._orders.onOrderFilterSearchChanged(text);
  public onOrderFilterTimeframeSelected = (timeframe: TimeframeFilter) =>
    this._orders.setTimeframeFilter(timeframe);
  public onOrderFilterRedeemTogglePressed = () =>
    this._orders.setShowRedeemed(!this._orders.showRedeemed);
  public onOrderFilterOutstandingTogglePressed = () =>
    this._orders.setShowOutstanding(!this._orders.showOutstanding);

  public onAdminVenuePayoutSummaryPressed(http: HttpClient) {
    (this._orders as OrderFactory).onAdminVenuePayoutSummaryPressed(http);
  }

  public onAdminDrinkPayoutSummaryPressed(http: HttpClient) {
    (this._orders as OrderFactory).onAdminDrinkPayoutSummaryPressed(http);
  }
  /**
   *
   * END ORDER & ANALYTICS SECTION
   *
   */
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /**
   *
   * BEGIN CITY SECTION
   *
   */
  protected _cities: CityFactory;
  protected _selectedCity: City;
  public get cities$(): Observable<City[]> {
    return this._cities.cities$;
  }
  public async getAllCities(): Promise<City[]> {
    return this._cities.getAllCities();
  }
  public get city(): City {
    return this.hasSelectedCity ? this._selectedCity : null;
  }
  private get hasSelectedCity(): boolean {
    return this._selectedCity != null;
  }
  public get cityBuilder(): CityBuilder {
    return this._cities.builder;
  }
  public onCitySelected(city: City) {
    this._selectedCity = city;
    this._venues = new VenueFactory(
      city,
      this.db,
      this.httpClient,
      this.authService,
      this.toast
    );
  }
  public onAddCityPressed = () => this._cities.create();
  public onEditCityPressed = (city: City) => this._cities.fix(city);
  public onSaveCityPressed = async (city: City) => this._cities.deploy(city);
  /**
   *
   * END CITY SECTION
   *
   */
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /**
   *
   * BEGIN VENUE SECTION
   *
   */
  protected _venues: VenueFactory;
  protected _selectedVenue: Venue;
  protected _venueSearchText: string;

  public get venues$(): Observable<Venue[]> {
    return this._venues.venues$;
  }
  public async getAllVenues(): Promise<Venue[]> {
    return this._venues.getAllVenues();
  }
  public get venue(): Venue {
    return this.hasSelectedVenue ? this._selectedVenue : null;
  }
  public get venueSearchText(): string {
    return this._venueSearchText;
  }
  private get hasSelectedVenue(): boolean {
    return this._selectedVenue != null;
  }
  public async onVenueRefreshed() {
    this._selectedVenue = await this._venues.refresh(
      this._selectedVenue.reference.id
    );
  }

  public onSaveVenue = async (venue: Venue) => {
    this._selectedVenue = await this._venues.saveVenue(venue);
  };

  public permissions = [];

  public async onVenueSelected(venue: Venue) {
    this._selectedVenue = venue;
    this._products = new ProductFactory(
        venue, 
        this.httpClient, 
        this.authService,
        this.toast,
    );
    this._orders = new VenueOrderFactory(venue, this.db);
    this._drinks = new MenuDrinkFactory(
      venue.id,
      venue.reference,
      this.db,
      this.toast,
      this.httpClient,
      this.authService
    );
    this._categories = new MenuCategoryFactory(
      venue.id,
      this.toast,
      this.httpClient,
      this.authService
    );
    this._events = new EventFactory(venue, this.db, this.httpClient, this.authService, this.toast);
    const httpHeaders = await this.getHTTPHeaders();
    this.permissions = await this.httpClient
        .get(
            `${environment.apiBaseUrl}/api/v1/permissions`,
            {
                headers: httpHeaders,
                params: {
                    userId: this.user.id,
                    venueId: venue.id,
                },
            },
        )
        .toPromise()
        .then((permissionsResponse) => {
            return permissionsResponse as string[];
        })
        .catch((error) => {
            this.toast.error("Could not get user permissions. Please try again.", "Error", {
                opacity: 1,
            });
            throw(error);
        });
  }

  public onAddVenue = async (venue: Venue) => {
    this._venues.addVenue(venue);
  };

  public onVenueSearchChanged(text: string) {
    this._venueSearchText = text;
    if (this.hasSelectedVenue) {
      this._venues = new VenueFactory(
        null,
        this.db,
        this.httpClient,
        this.authService,
        this.toast
      );
    }
  }

  public onVenuePayoutSummaryPressed(http: HttpClient) {
    this.drinkAnalytics.onVenuePayoutSummaryPressed(
      http,
      this.venue.isTipDistributionActive
    );
  }
  onTopSellersSummaryPressed(http: HttpClient) {
    this.drinkAnalytics.onTopSellersSummaryPressed(http);
  }
  onTransactionSummaryPressed(http: HttpClient) {
    this.drinkAnalytics.onTransactionsSummaryPressed(http);
  }
  /**
   *
   * END VENUE SECTION
   *
   */
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /**
   *
   * START PRODUCT SECTION
   *
   */

  protected _products: ProductFactory;

  public get passes$(): Observable<Product[]> {
    return this._products.passes$;
  }

  public get products$(): Observable<Product[]> {
    return this._products.products$;
  }

  public get productFilterProductType(): ProductType {
    return this._products.productQuery.productType;
  }

  public get productFilterScheduleType(): ScheduleType {
    return this._products.productQuery.scheduleType;
  }
  public get productFilterHiddenToggle(): boolean {
    return this._products.productQuery.showHidden;
  }
  public get passFilterSearchText(): string {
    return this._products.searchText;
  }

  public onAddProductPressed = () => this._products.create();
  public onPassFilterSearchChanged = (text: string) =>
    (this._products.searchText = text);
  public onEditProductDetailsPressed = (product: Product) =>
    this._products.fix(product);
  public onEditProductSchedulePressed = (product: Product) =>
    this._products.fix(product);
  public onProductFilterProductTypeChanged = (type: ProductType) =>
    this._products.setProductTypeFilter(type);
  public onProductFilterScheduleTypeChanged = (type: ScheduleType) =>
    this._products.setScheduleTypeFilter(type);
  public onProductFilterHiddenToggleChanged = () =>
    this._products.setHiddenFilter(!this._products.productQuery.showHidden);
  public onProductsRefreshed = () => this._products.refresh();
  /**
   *
   * END PRODUCT SECTION
   *
   */
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /**
   *
   * START DRINK SECTION
   *
   */
  protected _drinks: MenuDrinkFactory;
  public get drinkBuilder(): MenuDrinkBuilder {
    return this._drinks.builder;
  }

  public get drinks$(): Observable<MenuDrink[]> {
    return this._drinks.drinks$;
  }

  public get drinkOrders$(): Observable<MenuOrderData[]> {
    return this._drinks.drinkOrders$;
  }
  public get drinkAnalytics(): MenuSalesAnalytics {
    return this._drinks.analytics;
  }

  public get menuOrderFilterSelectedTimeframe(): TimeframeFilter {
    return this._drinks.menuOrderFilterSelectedTimeframe;
  }
  public get menuOrderFilterSelectedDrink(): MenuDrink {
    return this._drinks.menuOrderFilterSelectedDrink;
  }
  public get menuOrderFilterSelectedCategory(): MenuCategory {
    return this._drinks.menuOrderFilterSelectedCategory;
  }
  public get menuOrderFilterSearchText(): string {
    return this._drinks.menuOrderFilterSearchText;
  }
  public get menuOrderTableTotalDrinks(): number {
    return this._drinks.query.totalDrinks;
  }

  public onMenuOrderFilterTimeframeSelected = (tf: TimeframeFilter) =>
    this._drinks.onMenuOrderFilterTimeframeSelected(tf);
  public onMenuOrderExportPressed = (orders: MenuOrderData[]) =>
    this._drinks.onMenuOrderExportPressed(orders, this.isAdmin);
  public onMenuOrderFilterDrinkSelected = (drink: MenuDrink) =>
    this._drinks.onMenuOrderFilterDrinkSelected(drink);
  public onMenuOrderFilterCategorySelected = (category: MenuCategory) =>
    this._drinks.onMenuOrderFilterCategorySelected(category);
  public onMenuOrderFilterSearchChanged = (text: string) =>
    this._drinks.onMenuOrderFilterSearchChanged(text);
  public onMenuOrderFilterRefreshed = () =>
    this._drinks.onMenuOrderFilterRefreshed();

  public onDrinkTableAddPressed = () => this._drinks.create();
  public onDrinkTableSavePressed = () => this._drinks.deploy();
  public onDrinkTableDuplicatePressed = (drink: MenuDrink) =>
    this._drinks.copy(drink);

  public onDrinkTableEditPressed = (drink: MenuDrink) =>
    this._drinks.fix(drink);
  public onDrinkTableDeletePressed = (drink: MenuDrink) =>
    this._drinks.remove(drink);
  public onDrinkTableActiveTogglePressed = (drink: MenuDrink) =>
    this._drinks.toggleIsActive(drink);

  public async onDrinkTableDrinkRepositioned(
    drinks: MenuDrink[],
    category: MenuCategory
  ) {
    await this._drinks.reposition(drinks, category);
  }

  /**
   *
   * END DRINK SECTION
   *
   */
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /******************************************************************************************/
  /**
   *
   * START CATEGORY SECTION
   *
   */
  protected _categories: MenuCategoryFactory;

  public get categories$(): Observable<MenuCategory[]> {
    return this._categories.categories$;
  }

  public get categoryBuilder(): MenuCategoryBuilder {
    return this._categories.builder;
  }
  public getCategoryDescription = (categories: MenuCategory[]) =>
    this._categories.getDescription(categories);

  public onCategoryTableDeletePressed = (category: MenuCategory) => {
    this._categories.remove(category);
  };

  public onCategoryTableActiveTogglePressed = (category: MenuCategory) =>
    this._categories.toggleIsActive(category);

  public onCategoryTableEditPressed = (category: MenuCategory) => {
    this._categories.fix(category);
  };
  public onCategoryPositionUpdated = async (
    category: MenuCategory,
    updatedPosition: number
  ) => {
    this._categories.updatePosition(category, updatedPosition);
  };
  public onCategoryTableAddPressed = () => this._categories.create();
  public onSaveCategoryPressed = () => this._categories.deploy();

  /**
   *
   * END CATEGORY SECTION
   *
   */

  /**
   *
   * START EVENT SECTION
   *
   */
  protected _events: EventFactory;

  public onCalendarTabPressed = () => this._events.refresh();

  public getEventsForDate = (date: string) =>
    this._events.getEventsForDate(date);

  public onPreviousCalendarMonthPressed = () =>
    this._events.onPreviousCalendarMonthPressed();

  public onNextCalendarMonthPressed = () =>
    this._events.onNextCalendarMonthPressed();

  public onPassesRefreshed = (date: string, passes: Pass[]) =>
    this._events.setPassesForDate(date, passes);

  /**
   *
   * END EVENT SECTION
   *
   */

  /**
   *
   * START USER SECTION
   *
   */
  public _users: UserFactory;

  /**
   *
   * END USER SECTION
   *
   */
}
